import { useMemo } from 'react';
import { ApolloError } from '@apollo/client';

import {
  ContractDocumentCardQuery,
  ContractDocumentsListQuery,
  ContractDocumentsListQueryVariables,
  useContractDocumentCardQuery,
  useContractDocumentsListQuery,
} from 'core/graphql';

import { useStableRefetch } from '../Groups/useStableRefetch';
import { usePageVisiblePoll } from '../Meta/usePageVisiblePoll';
import { isEsignEmailPending, isEsignInFlight } from './esignModel';
import { useEsignPermissions } from './useEsignPermissions';

export type IContractDocumentRow = ContractDocumentsListQuery['contractDocuments'][number];

export type IContractDocumentCard = NonNullable<
  ContractDocumentCardQuery['contractDocument']
>;

export type IContractDocumentsFilters = Omit<
  ContractDocumentsListQueryVariables,
  'limit' | 'offset'
>;

/** Опрос раз в 5 с — только пока на экране есть строка «в пути». */
export const ESIGN_POLL_MS = 5000;

const EMPTY_ROWS: IContractDocumentRow[] = [];

export interface IUseContractDocuments {
  rows: IContractDocumentRow[];
  total: number;
  /** Сколько договоров в каждом статусе при прочих фильтрах. */
  statusCounts: Record<string, number>;
  loading: boolean;
  error?: ApolloError;
  refetch: () => void;
}

/**
 * Список договоров с фильтрами и страницей (`limit`/`offset`).
 *
 * Страница — это ПЕРЕМЕННЫЕ запроса, а не `fetchMore`-склейка: опрос и
 * перезапрос перечитывают ровно текущую страницу и не схлопывают
 * накопленное (грабли `apollo-refetch-collapses-pages`).
 *
 * При смене фильтра данные для новых переменных ещё не пришли — таблица
 * держит прошлую выдачу (`previousData`), а не мигает пустотой: иначе
 * пустое состояние на полсекунды читается как «ничего не нашлось».
 *
 * `enabled=false` — вызывающий не имеет прав на домен (карточка, которую
 * открывает посторонняя роль): запрос не уходит вовсе.
 */
export const useContractDocuments = (
  filters: IContractDocumentsFilters,
  limit: number,
  offset: number,
  enabled = true,
): IUseContractDocuments => {
  const { canOpenPage } = useEsignPermissions();
  const skip = !enabled || !canOpenPage;
  const {
    data,
    previousData,
    loading,
    error,
    refetch,
    startPolling,
    stopPolling,
  } = useContractDocumentsListQuery({
    variables: { ...filters, limit, offset },
    skip,
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: false,
  });

  const shown = data ?? previousData;
  const rows = shown?.contractDocuments ?? EMPTY_ROWS;
  const counts = shown?.contractDocumentStatusCounts;
  const statusCounts = useMemo(
    () =>
      Object.fromEntries((counts ?? []).map((row) => [row.status, row.count])),
    [counts],
  );
  const inFlight = rows.some(isEsignInFlight);

  usePageVisiblePoll(
    startPolling,
    stopPolling,
    ESIGN_POLL_MS,
    !skip && inFlight,
  );

  return {
    rows,
    total: shown?.contractDocumentsTotal ?? 0,
    statusCounts,
    loading,
    error,
    refetch: useStableRefetch(refetch),
  };
};

export interface IUseContractDocument {
  contract: IContractDocumentCard | null;
  loading: boolean;
  error?: ApolloError;
  refetch: () => void;
}

/**
 * Карточка одного договора (ящик «Договор»).
 *
 * Опрашивается, пока идёт отправка или зачисление: оба процесса живут в
 * Celery, и без опроса человек смотрел бы на «Отправляется» до перезагрузки.
 */
export const useContractDocument = (
  id: string | null,
): IUseContractDocument => {
  const { canOpenPage } = useEsignPermissions();
  const skip = !id || !canOpenPage;
  const {
    data,
    loading,
    error,
    refetch,
    startPolling,
    stopPolling,
  } = useContractDocumentCardQuery({
    variables: { id: id || '' },
    skip,
    fetchPolicy: 'cache-and-network',
  });

  const contract = data?.contractDocument ?? null;
  usePageVisiblePoll(
    startPolling,
    stopPolling,
    ESIGN_POLL_MS,
    !skip &&
      Boolean(
        contract &&
          (isEsignInFlight(contract) || isEsignEmailPending(contract)),
      ),
  );

  return {
    contract,
    loading,
    error,
    refetch: useStableRefetch(refetch),
  };
};
